<template>
    <div
        v-if="visible"
        ref="floating"
        class="floating-container"
        :style="{
            ...floatingStyles,
            visibility: middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
        }">
        <div
            v-show="component === 'toolbar'"
            class="ai-toolbar-floating">
            <slot name="toolbarItems" />
        </div>
        <div
            v-show="component === 'modal'"
            class="ai-editor-panel">
            <div class="ai-editor-panel__header">
                <h3 class="ai-editor-panel__title">
                    <pendo-icon
                        type="wand"
                        size="12" />
                    {{ $t('guidesConversationalAiModal.AI editor') }}
                </h3>
                <button
                    class="ai-editor-panel__close"
                    @click="updateCurrentComponent('toolbar')">
                    <pendo-icon
                        type="x"
                        :size="20" />
                </button>
            </div>
            <div
                v-for="section in sections"
                :key="section.type"
                class="ai-editor-panel__body">
                <h4 class="ai-editor-panel__subtitle">
                    {{ section.titleKey }}
                </h4>
                <div
                    v-for="option in section.options"
                    :key="option.id"
                    class="editor-menu-item"
                    @click="onItemClick(option.id, targetElement)">
                    <pendo-icon
                        :type="option.icon"
                        :size="16" />
                    <div>
                        {{ option.label }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { PendoIcon } from '@pendo/components';
import { useFloating, offset, flip, autoUpdate, hide } from '@floating-ui/vue';
import { ref, watch } from 'vue';

export default {
    name: 'GuidesConversationalAiModal',
    components: {
        PendoIcon,
    },
    props: {
        targetElement: {
            type: Object,
            default: null,
        },
        visible: {
            type: Boolean,
            default: false,
        },
        component: {
            type: String,
            default: 'toolbar',
        },
    },
    emits: ['close', 'request-content-change', 'update-component'],
    setup (props) {
        const reference = ref(null);
        const floating = ref(null);

        const { floatingStyles, update, middlewareData } = useFloating(reference, floating, {
            placement: 'right-start',
            middleware: [
                offset(8),
                flip(),
                hide(),
            ],
            whileElementsMounted: autoUpdate,
        });

        watch(
            () => props.targetElement,
            (newTarget) => {
                reference.value = newTarget;
                update();
            },
            { immediate: true },
        );

        watch(
            () => props.visible,
            (isVisible) => {
                if (isVisible && props.targetElement) {
                    reference.value = props.targetElement;
                    update();
                }
            },
        );

        return {
            reference,
            floating,
            floatingStyles,
            middlewareData,
        };
    },
    data () {
        return {
            lengthEditorOptions: [
                {
                    id: 'makeShorter',
                    label: this.$t('guidesConversationalAiModal.Make shorter'),
                    icon: 'minimize-2',
                },
                {
                    id: 'makeLonger',
                    label: this.$t('guidesConversationalAiModal.Make longer'),
                    icon: 'maximize-2',
                },
            ],
            toneEditorOptions: [
                {
                    id: 'makeProfessional',
                    label: this.$t('guidesConversationalAiModal.Professional'),
                    icon: 'briefcase',
                },
                {
                    id: 'makeCasual',
                    label: this.$t('guidesConversationalAiModal.Casual'),
                    icon: 'hand-metal',
                },
                {
                    id: 'makeConversational',
                    label: this.$t('guidesConversationalAiModal.Conversational'),
                    icon: 'message-circle',
                },
                {
                    id: 'makeDirect',
                    label: this.$t('guidesConversationalAiModal.Direct'),
                    icon: 'target',
                },
                {
                    id: 'makeExpert',
                    label: this.$t('guidesConversationalAiModal.Expert'),
                    icon: 'beaker',
                },
                {
                    id: 'makeFriendly',
                    label: this.$t('guidesConversationalAiModal.Friendly'),
                    icon: 'laugh',
                },
            ],
        };
    },
    computed: {
        sections () {
            return [
                {
                    type: 'length',
                    titleKey: this.$t('guidesConversationalAiModal.Edit length'),
                    options: this.lengthEditorOptions,
                },
                {
                    type: 'tone',
                    titleKey: this.$t('guidesConversationalAiModal.Change tone'),
                    options: this.toneEditorOptions,
                },
            ];
        },
    },
    created () {
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount () {
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        updateCurrentComponent (component) {
            this.$emit('update-component', component);
        },
        handleClickOutside (event) {
            if (!this.visible) return;

            const isClickOnComponent = event.target.closest('.floating-container');
            if (isClickOnComponent) return;

            this.$emit('close');
        },
        onItemClick (actionId, targetElement) {
            this.$emit('request-content-change', { text: targetElement.textContent, action: actionId });
        },
    },
};
</script>

<style lang="less" scoped>
.floating-container {
    z-index: 1000;
}

.ai-editor-panel {
    width: 211px;
    border-radius: 3px;
    border: 1px solid var(--palette-gray-40);
    background: var(--palette-gray-0);
    box-shadow: 0 4px 4px 0 var(--semantic-drop-shadow-base);

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 16px;
        align-self: stretch;
        border-bottom: 1px solid var(--palette-gray-40);
    }

    &__title {
        display: inline-grid;
        grid-template-columns: 1fr 3fr;
        align-items: center;
        color: var(--palette-gray-100);
        font-family: Inter;
        font-size: 14.222px;
        font-style: normal;
        font-weight: 600;
        margin: 0;
    }

    &__subtitle {
        display: flex;
        padding: 8px 16px;
        align-items: center;
        gap: 8px;
        align-self: stretch;
        margin: 0;
        color: var(--palette-gray-70);
        font-family: Inter;
        font-size: 10px;
        font-style: normal;
        font-weight: 600;
        line-height: 120%;
        letter-spacing: 2px;
        text-transform: uppercase;
    }

    &__body {
        cursor: pointer;
        padding: 8px 0;
        border-bottom: 1px solid var(--palette-gray-40);
    }

    &__close {
        background: none;
        border: none;
        padding: 4px;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: center;

        &:hover {
            background-color: var(--palette-gray-20);
        }
    }
}

.ai-toolbar-floating {
    display: flex;
    align-items: center;
    padding: 0px;
    border: 1px solid var(--palette-gray-40);
    background: var(--palette-gray-0);
    box-shadow: 0px 4px 4px var(--semantic-drop-shadow-base);
    border-radius: 3px;
}

.editor-menu-item {
    display: flex;
    padding: 4px 24px 4px 16px;
    align-items: center;
    align-self: stretch;
    cursor: pointer;

    &:hover {
        background-color: var(--palette-gray-20);
    }

    .pendo-icon {
        margin-right: 12px;
    }
}
</style>
